import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, Loader2 } from "lucide-react";
import { fetchDates } from "../lib/api";
import { MONTH_NAMES, CATEGORY_LABELS } from "../lib/format";

const RESULT_LIMIT = 8;

export const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Reset and focus when overlay opens
  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setActiveIdx(0);
      // small delay so the input exists before focusing
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      document.body.style.overflow = "hidden";
      return () => {
        clearTimeout(t);
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  // Debounced live search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      fetchDates({ q: term, limit: RESULT_LIMIT })
        .then((data) => {
          setResults(data.slice(0, RESULT_LIMIT));
          setActiveIdx(0);
        })
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(handle);
  }, [q, open]);

  const goToResult = (entry) => {
    onClose();
    navigate(`/date/${entry.id}`);
  };

  const goToExplorer = () => {
    onClose();
    const term = q.trim();
    if (term) {
      navigate(`/?q=${encodeURIComponent(term)}#explorer`);
    } else {
      navigate(`/#explorer`);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (!results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = results[activeIdx];
      if (target) goToResult(target);
      else goToExplorer();
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="search-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search the archive"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "hsl(var(--bg-deep) / 0.78)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "clamp(1rem, 8vh, 5rem) 1rem 1rem",
        animation: "wnbf-fade-in 180ms ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(720px, 100%)",
          background: "hsl(var(--bg))",
          border: "1px solid hsl(var(--line-strong))",
          boxShadow: "0 24px 60px hsl(var(--shadow) / 0.35)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "1rem 1.1rem",
            borderBottom: "1px solid hsl(var(--line))",
            background: "hsl(var(--bg-elev))",
          }}
        >
          <Search size={18} aria-hidden style={{ color: "hsl(var(--accent))" }} />
          <input
            ref={inputRef}
            data-testid="search-overlay-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search by name, person, theme, or year…"
            aria-label="Search the archive"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              color: "hsl(var(--ink))",
              fontFamily: "var(--font-body)",
              fontSize: "1.05rem",
            }}
          />
          {loading && <Loader2 size={16} aria-hidden style={{ color: "hsl(var(--muted))", animation: "wnbf-spin 0.9s linear infinite" }} />}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close search"
            data-testid="search-overlay-close"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 30,
              border: "1px solid hsl(var(--line))",
              background: "hsl(var(--bg))",
              color: "hsl(var(--ink-soft))",
              cursor: "pointer",
              borderRadius: 999,
            }}
          >
            <X size={14} aria-hidden />
          </button>
        </div>

        <div
          data-testid="search-overlay-results"
          style={{ maxHeight: "min(60vh, 480px)", overflowY: "auto" }}
        >
          {q.trim().length < 2 && (
            <Hint>Type at least 2 characters to search across 132 archive entries.</Hint>
          )}
          {q.trim().length >= 2 && !loading && results.length === 0 && (
            <Hint>No matches for &ldquo;{q.trim()}&rdquo;. Try a different name, theme, or year.</Hint>
          )}
          {results.map((entry, i) => {
            const active = i === activeIdx;
            return (
              <button
                key={entry.id}
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => goToResult(entry)}
                data-testid={`search-result-${entry.id}`}
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 16,
                  padding: "0.95rem 1.1rem",
                  background: active ? "hsl(var(--bg-elev))" : "transparent",
                  border: "none",
                  borderBottom: "1px solid hsl(var(--line))",
                  cursor: "pointer",
                  textAlign: "left",
                  color: "hsl(var(--ink))",
                  transition: "background-color 120ms ease",
                }}
              >
                <span
                  aria-hidden
                  style={{
                    fontFamily: "var(--font-display)",
                    fontStyle: "italic",
                    fontSize: "0.78rem",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "hsl(var(--accent))",
                    minWidth: 70,
                  }}
                >
                  {formatDateChip(entry)}
                </span>
                <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "1.05rem",
                      lineHeight: 1.25,
                      color: "hsl(var(--ink))",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {entry.title}
                  </span>
                  <span
                    style={{
                      fontSize: "0.82rem",
                      color: "hsl(var(--muted))",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {formatTags(entry)}
                  </span>
                </span>
                <ArrowRight
                  size={14}
                  aria-hidden
                  style={{
                    color: active ? "hsl(var(--accent))" : "hsl(var(--muted-2))",
                    transition: "color 120ms ease",
                  }}
                />
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            padding: "0.7rem 1.1rem",
            borderTop: "1px solid hsl(var(--line))",
            background: "hsl(var(--bg-elev))",
            fontSize: "0.74rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "hsl(var(--muted))",
          }}
        >
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> navigate &nbsp; · &nbsp; <Kbd>↵</Kbd> open &nbsp; · &nbsp; <Kbd>esc</Kbd> close
          </span>
          {q.trim() && (
            <button
              type="button"
              onClick={goToExplorer}
              data-testid="search-overlay-see-all"
              style={{
                fontSize: "0.74rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "hsl(var(--accent))",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: "var(--font-body)",
                fontWeight: 600,
              }}
            >
              See all in explorer →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const Hint = ({ children }) => (
  <div
    data-testid="search-overlay-hint"
    style={{
      padding: "1.5rem 1.1rem",
      fontFamily: "var(--font-display)",
      fontStyle: "italic",
      fontSize: "0.95rem",
      color: "hsl(var(--muted))",
    }}
  >
    {children}
  </div>
);

const Kbd = ({ children }) => (
  <kbd
    style={{
      display: "inline-block",
      padding: "1px 6px",
      border: "1px solid hsl(var(--line-strong))",
      borderRadius: 3,
      fontFamily: "var(--font-body)",
      fontSize: "0.7rem",
      color: "hsl(var(--ink-soft))",
      background: "hsl(var(--bg))",
      letterSpacing: 0,
      textTransform: "none",
    }}
  >
    {children}
  </kbd>
);

const formatDateChip = (entry) => {
  if (entry.month && entry.day) {
    const m = MONTH_NAMES[entry.month] || "";
    return `${m.slice(0, 3)} ${entry.day}`;
  }
  if (entry.month) {
    const m = MONTH_NAMES[entry.month] || "";
    return m.slice(0, 3);
  }
  if (entry.year_established) return String(entry.year_established);
  return "—";
};

const formatTags = (entry) => {
  const cats = (entry.categories || []).map((c) => CATEGORY_LABELS[c] || c);
  const recog = entry.recognition_status ? [entry.recognition_status] : [];
  return [...recog, ...cats].slice(0, 3).join(" · ") || "Archive entry";
};
